import { html, TemplateResult, LitElement, svg, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import globalStyles from './outline-alert.global.pcss?inline';
import encapsulatedStyles from './outline-alert.inner.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { alertIcons } from './assets/Icons';
import '../outline-heading/outline-heading';
import { classMap } from 'lit/directives/class-map.js';
/**
 * Defines the type for an object containing SVG icons.
 */
export interface IconSet {
  [key: string]: ReturnType<typeof svg>;
}

export const icons: IconSet = Object.assign({}, alertIcons);

/**
 * Defines the type for alert types. based on common alerts
 * Can be 'success', 'info', 'warning', or 'error'.
 */
export type AlertType =
  | 'success'
  | 'info'
  | 'warning'
  | 'error'
  | 'announcement';

/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-alert';

/**
 * The Outline Core Alert component
 *
 * A reusable LitElement component for displaying alert.
 *
 * @element outline-alert
 * @extends LitElement
 * @slot - icon for the alert type
 * @slot - heading for the alert
 * @slot - body for the alert
 * @slot - closing for the close button when the type is dismissible
 */
@customElement(componentName)
export class OutlineAlert extends LitElement {
  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  @property({ type: String, reflect: true, attribute: 'alert-type' })
  alertType: AlertType = 'info';

  @property({ type: Boolean, reflect: true, attribute: 'dismissible' })
  dismissible = false;

  /**
   * Retrieves the icon for a given icon name.
   * @param iconName - The name of the icon.
   * @returns A TemplateResult representing the icon.
   */
  getIcon(iconName: string): TemplateResult | undefined {
    return icons[iconName];
  }

  render() {
    const classType = 'alert--' + this.alertType;
    const classes = {
      alert: true,
      [classType]: true,
    };
    /** These classes are for styling the div on the component */
    const alertIcon = {
      'alert-icon': true,
    };
    const alertContent = {
      'alert-content': true,
    };

    const buttonClass = {
      /** This classes are based on Sitewide_alert module for drupal
       * Note: in Storybook it will only indicate where the button will be placed
       * and it will not work until it's on drupal.
       */
      'dismiss-button': true,
      'js-dismiss-button': true,
    };
    return html`<div class=${classMap(classes)}>
      <div class=${classMap(alertIcon)}>
        <slot name="icon">${this.getIcon(this.alertType)}</slot>
      </div>
      <div class=${classMap(alertContent)}>
        <slot name="heading"></slot>
        <slot name="body"></slot>
      </div>
      ${this.dismissible
        ? html`<slot name="closing"
            ><button class=${classMap(buttonClass)} aria-label="Dismiss alert">
              ${this.getIcon('close')}
            </button></slot
          >`
        : nothing}
    </div>`;
  }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineAlert;
  }
}
